/**
 * Homepage ("My Transactions") client-side search helpers.
 *
 * Match rules:
 * - Case-insensitive; punctuation stripped; whitespace collapsed
 * - Street suffixes (Street↔St, Drive↔Dr) abbreviated only on address fields and
 *   on the query — never rewritten inside person names
 * - Tokens AND'd against whole words (short suffix abbrevs are exact-word only
 *   so "Drive"→"dr" cannot match inside "Andrew")
 * - Longer tokens may prefix-match a haystack word ("brad" → "bradley")
 */

const STREET_SUFFIX_TO_ABBREV = [
  [/\bboulevard\b/g, 'blvd'],
  [/\bparkway\b/g, 'pkwy'],
  [/\bhighway\b/g, 'hwy'],
  [/\bterrace\b/g, 'ter'],
  [/\bcircle\b/g, 'cir'],
  [/\bcirc\b/g, 'cir'],
  [/\bstreet\b/g, 'st'],
  [/\bavenue\b/g, 'ave'],
  [/\bdrive\b/g, 'dr'],
  [/\bcourt\b/g, 'ct'],
  [/\bplace\b/g, 'pl'],
  [/\broad\b/g, 'rd'],
  [/\blane\b/g, 'ln'],
  [/\btrail\b/g, 'trl'],
  [/\bcove\b/g, 'cv'],
];

const STREET_ABBREV_TOKENS = new Set([
  'blvd', 'pkwy', 'hwy', 'ter', 'cir', 'circ', 'st', 'ave', 'dr', 'ct', 'pl', 'rd', 'ln', 'trl', 'cv',
]);

/** Lowercase + strip punctuation + collapse whitespace (no suffix rewriting). */
export function normalizeBasic(text) {
  if (text == null) return '';
  return String(text)
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Rewrite full street-suffix words to abbreviations (address / query only). */
export function abbreviateStreetSuffixes(text) {
  let s = normalizeBasic(text);
  if (!s) return '';
  for (const [pattern, abbrev] of STREET_SUFFIX_TO_ABBREV) {
    s = s.replace(pattern, abbrev);
  }
  return s.replace(/\s+/g, ' ').trim();
}

/** @deprecated Prefer normalizeBasic / abbreviateStreetSuffixes; kept for tests. */
export function normalizeForSearch(text) {
  return abbreviateStreetSuffixes(text);
}

function pushField(parts, value) {
  if (value == null) return;
  if (Array.isArray(value)) {
    value.forEach((v) => pushField(parts, v));
    return;
  }
  const raw = String(value).trim();
  if (!raw) return;
  parts.push(raw);
}

function pushAddressField(addressParts, value) {
  pushField(addressParts, value);
}

function tokenize(normalized) {
  return normalized.split(' ').filter(Boolean);
}

function addWords(wordSet, text, { abbreviate = false } = {}) {
  const normalized = abbreviate ? abbreviateStreetSuffixes(text) : normalizeBasic(text);
  tokenize(normalized).forEach((w) => wordSet.add(w));
}

/**
 * Collect searchable whole-words for one homepage entity.
 * @param {'contract'|'listing'|'buyer_agreement'|'referral'} type
 * @param {object} item
 * @returns {Set<string>}
 */
export function buildSearchWordSet(type, item) {
  const words = new Set();
  if (!item) return words;

  const general = [];
  const address = [];

  if (type === 'contract') {
    pushAddressField(address, item.property_address);
    pushAddressField(address, item.property_address_city);
    pushAddressField(address, item.property_address_state);
    pushAddressField(address, item.property_address_zip_code);
    pushAddressField(address, item.property_address_county);
    pushField(general, item.buyer_name);
    pushField(general, item.seller_name);
    pushField(general, item.buyer_agent_name);
    pushField(general, item.sellers_agent_name);
    pushField(general, item.buyer_agent_email);
    pushField(general, item.sellers_agent_email);
    pushField(general, item.team_member_names);
    pushField(general, item.team_member_emails);
  } else if (type === 'listing') {
    pushAddressField(address, item.street_address);
    pushAddressField(address, item.city);
    pushAddressField(address, item.state);
    pushAddressField(address, item.zip_code);
    pushAddressField(address, item.county);
    pushField(general, item.mls_number);
    (item.parties || []).forEach((p) => {
      pushField(general, p?.name);
      pushField(general, p?.email);
    });
  } else if (type === 'buyer_agreement') {
    pushAddressField(address, item.street_address);
    pushAddressField(address, item.city);
    pushAddressField(address, item.state);
    pushAddressField(address, item.zip_code);
    (item.parties || []).forEach((p) => {
      pushField(general, p?.name);
      pushField(general, p?.email);
    });
  } else if (type === 'referral') {
    pushAddressField(address, item.property_address);
    pushField(general, item.receiving_agent_name);
    pushField(general, item.referring_agent_name);
    pushField(general, item.receiving_agent_email);
    pushField(general, item.referring_agent_email);
  }

  general.forEach((part) => addWords(words, part, { abbreviate: false }));
  // Address fields: keep original tokens and abbreviated suffix forms.
  address.forEach((part) => {
    addWords(words, part, { abbreviate: false });
    addWords(words, part, { abbreviate: true });
  });

  return words;
}

/** Joined haystack string (tests / debugging). */
export function buildSearchHaystack(type, item) {
  return [...buildSearchWordSet(type, item)].join(' ');
}

function tokenMatchesWordSet(words, token) {
  if (!token) return true;
  // Short / street-abbrev tokens must be exact words — avoids "dr" in "andrew".
  if (token.length <= 2 || STREET_ABBREV_TOKENS.has(token)) {
    return words.has(token);
  }
  if (words.has(token)) return true;
  for (const word of words) {
    if (word.startsWith(token)) return true;
  }
  return false;
}

/**
 * True when every query token matches a haystack word.
 *
 * Each basic token may match either its original form (names like "Lane") or
 * its street-suffix abbreviation (addresses like "Buyer Lane" ↔ "ln").
 */
export function matchesSearch(type, item, searchTerm) {
  const basicQuery = normalizeBasic(searchTerm);
  if (!basicQuery) return true;
  const tokens = tokenize(basicQuery);
  if (!tokens.length) return true;
  const words = buildSearchWordSet(type, item);
  return tokens.every((token) => {
    if (tokenMatchesWordSet(words, token)) return true;
    const abbreviated = abbreviateStreetSuffixes(token);
    return Boolean(abbreviated) && abbreviated !== token && tokenMatchesWordSet(words, abbreviated);
  });
}

export const PORTAL_SEARCH_PLACEHOLDER =
  'Search (address, city, buyer, seller, agent)...';
